import { gridPaths } from '@/shared/hooks';
import { createEntityGridConfig, type GridConfig } from '@/ui/composites/grid/gridConfigFactory';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { fetchSuiteExecutionsGrid } from '../grid/suiteExecutionsGridQuery';
import { fetchTestExecutionsGrid } from '../grid/testExecutionsGridQuery';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridFilterModel, GridSortModel } from '@/ui/primitives';
import type { SuiteExecutionDto, TestExecutionDto } from '@flowqa/shared';

type ExecutionGridConfig<TDto> = GridConfig<TDto>;

export const suiteExecutionDefaultSortModel: GridSortModel = [{ field: 'timestamp', sort: 'desc' }];
export const testExecutionDefaultSortModel: GridSortModel = [{ field: 'timestamp', sort: 'desc' }];

export const buildSuiteExecutionFilters = (suiteId: string): GridQueryInput['filters'] =>
  buildEqFilter('suiteId', suiteId);

export const buildTestExecutionFilters = (testId: string): GridQueryInput['filters'] =>
  buildEqFilter('testId', testId);

export const suiteExecutionsGridConfig = ({
  projectId,
  organisationId,
  fixedFilters,
  initialSortModel = suiteExecutionDefaultSortModel,
  initialFilterModel,
  persistKey,
  syncStateToUrl = false,
}: {
  projectId?: string | null;
  organisationId?: string | null;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
} = {}): ExecutionGridConfig<SuiteExecutionDto> =>
  createEntityGridConfig<SuiteExecutionDto>({
    path: gridPaths.suiteExecutions,
    queryFn: async (input) => await fetchSuiteExecutionsGrid(input, projectId, organisationId),
    noRowsText: 'No suite executions found',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    persistKey,
    defaultPersistKey: 'suite-executions',
    syncStateToUrl,
  });

export const testExecutionsGridConfig = ({
  projectId,
  organisationId,
  fixedFilters,
  initialSortModel = testExecutionDefaultSortModel,
  initialFilterModel,
  persistKey,
  syncStateToUrl = false,
}: {
  projectId?: string | null;
  organisationId?: string | null;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
} = {}): ExecutionGridConfig<TestExecutionDto> =>
  createEntityGridConfig<TestExecutionDto>({
    path: gridPaths.testExecutions,
    queryFn: async (input) => await fetchTestExecutionsGrid(projectId, organisationId, input),
    noRowsText: 'No test executions found',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    persistKey,
    defaultPersistKey: 'test-executions',
    syncStateToUrl,
  });

export type SuiteExecutionsGridConfig = ReturnType<typeof suiteExecutionsGridConfig>;
export type TestExecutionsGridConfig = ReturnType<typeof testExecutionsGridConfig>;
